import { Stack } from '@mui/joy';
import { type ReactElement, useEffect, useMemo } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import FormInput from 'components/technical/form/FormInput.tsx';
import type { GInputProps } from 'components/technical/inputs/GInput/GInput.props.ts';
import GInput from 'components/technical/inputs/GInput/GInput.tsx';

import type { AssetOptimizerInputFields } from './asset/AssetOptimizer.validation.ts';
import { useValueChanged } from '../../../UseValueChanged.tsx';

import { ConstraintType } from './ConstraintTypeValues.validation.ts';
import type { PortfolioOptimizerInputFields } from './portfolio/PortfolioOptimizer.validation.ts';

export const ConstraintValueInputs = ({
  pathPrefix,
  startAdornment,
  width,
}: {
  pathPrefix: FieldPath<AssetOptimizerInputFields | PortfolioOptimizerInputFields>;
  startAdornment?: string;
  width: GInputProps['width'];
}): ReactElement => {
  const { setValue, clearErrors, trigger } = useFormContext<AssetOptimizerInputFields>();

  const constraintType = useWatch({
    name: `${pathPrefix}.constraintType`,
  });

  const allValueFields = useMemo(() => {
    return [
      `${pathPrefix}.constraintValue.min`,
      `${pathPrefix}.constraintValue.max`,
      `${pathPrefix}.constraintValue.value`,
    ] as const;
  }, [pathPrefix]);

  const constraintChanged = useValueChanged(constraintType);
  useEffect(() => {
    if (!constraintChanged) {
      return;
    }

    for (const path of allValueFields) {
      // @ts-expect-error it's not possible to type the expriession, because path present in one optimizer may not be present in the other one
      setValue(path, null);
    }
    // @ts-expect-error it's not possible to type the expriession, because path present in one optimizer may not be present in the other one
    clearErrors(allValueFields);
  }, [constraintChanged, clearErrors, setValue, allValueFields]);

  if (constraintType === ConstraintType.None) {
    return <GInput disabled value="" width={width} />;
  }

  if (constraintType === ConstraintType.Equal) {
    return (
      <FormInput<AssetOptimizerInputFields>
        key="value"
        // @ts-expect-error it's not possible to type the expriession, because path present in one optimizer may not be present in the other one
        name={`${pathPrefix}.constraintValue.value` as const}
        type="number"
        startDecorator={startAdornment}
        label="Value"
        width={width}
      />
    );
  }
  if (constraintType === ConstraintType.Between) {
    return (
      <Stack direction="row" spacing={1.5}>
        <FormInput<AssetOptimizerInputFields>
          key="min"
          // @ts-expect-error it's not possible to type the expriession, because path present in one optimizer may not be present in the other one
          name={`${pathPrefix}.constraintValue.min` as const}
          type="number"
          startDecorator={startAdornment}
          label="Min"
          width={width}
          onChange={() => {
            // @ts-expect-error it's not possible to type the expriession, because path present in one optimizer may not be present in the other one
            trigger(`${pathPrefix}.constraintValue.max`);
          }}
        />
        <FormInput<AssetOptimizerInputFields>
          key="max"
          // @ts-expect-error it's not possible to type the expriession, because path present in one optimizer may not be present in the other one
          name={`${pathPrefix}.constraintValue.max` as const}
          type="number"
          startDecorator={startAdornment}
          label="Max"
          width={width}
          onChange={() => {
            // @ts-expect-error it's not possible to type the expriession, because path present in one optimizer may not be present in the other one
            trigger(`${pathPrefix}.constraintValue.min`);
          }}
        />
      </Stack>
    );
  }

  return <></>;
};
